/**
 * Signup page — /signup
 *
 * PRE-72: Public SMS-consent signup surface required to unblock Twilio A2P 10DLC
 * campaign QE2c6890da8086d771620e9b13fadeba0b and Toll-Free verification
 * HH1ef6dea39aa7428b1cb794ad0488ceb7 (both were rejected with codes 30909/30511
 * because reviewers could not find a public SMS opt-in surface on prest0.ai).
 *
 * Unlike /start (trial-purchase flow that assumes an access code from Sales),
 * /signup is a purely public anonymous flow: anyone can create a Prest0n
 * account here, opt in to SMS, and trigger a Twilio double-opt-in confirmation.
 *
 * What a send does (the account, the consent record, the double opt-in text,
 * where the person lands) is src/hooks/useSignupForm.ts, and every word is
 * src/components/auth/signupWords.ts: the new design's rebuild runs the same
 * two (design-system §7b rule 1). This file is today's look.
 *
 * Accessibility: real <label> bindings, aria-live error region, tab order
 * follows visual order, submit button disabled until the form is fully valid.
 *
 * This page is public — it MUST NOT be wrapped in ProtectedRoute.
 */

import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import PasswordInput from '../components/common/PasswordInput';
import { useSignupForm } from '../hooks/useSignupForm';
import { SIGNUP_WORDS as W } from '../components/auth/signupWords';

// ============================================================================
// Styling (matches existing Prest0 public-page aesthetic — StartTrial / Privacy)
// ============================================================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Page = styled.main`
  background: var(--brand-canvas);
  min-height: 100vh;
  padding: 0 0 5rem;
`;

const Hero = styled.section`
  max-width: 560px;
  margin: 0 auto;
  padding: 5rem 1.5rem 1.5rem;
  text-align: center;
  animation: ${fadeIn} 0.45s ease both;
`;

const HeroTitle = styled.h1`
  font-family: 'Space Grotesk', 'Inter', -apple-system, sans-serif;
  font-size: 2.1rem;
  font-weight: 500;
  letter-spacing: -0.03em;
  color: var(--brand-text);
  line-height: 1.2;
  margin-bottom: 0.6rem;
`;

const HeroSub = styled.p`
  font-size: 1rem;
  color: #666;
  line-height: 1.6;
  margin: 0;
`;

const FormCard = styled.form`
  max-width: 520px;
  margin: 1.5rem auto 0;
  padding: 1.5rem;
  background: #fff;
  border: 1px solid #E6E4DE;
  border-radius: 1rem;
  animation: ${fadeIn} 0.5s 0.08s ease both;

  @media (min-width: 640px) {
    padding: 2rem;
  }
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 1rem;
`;

const FieldLabel = styled.label`
  font-size: 0.82rem;
  font-weight: 600;
  color: #333;
  letter-spacing: -0.005em;
`;

const FieldHint = styled.span`
  font-size: 0.72rem;
  color: var(--brand-text-muted);
  font-weight: 400;
`;

const FieldInput = styled.input`
  border: 1px solid #E0DED8;
  border-radius: 0.6rem;
  padding: 0.75rem 0.9rem;
  font-size: 0.95rem;
  font-family: inherit;
  background: #fff;
  color: var(--brand-text);
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    border-color: var(--brand-text);
    box-shadow: 0 0 0 3px rgba(17, 17, 17, 0.08);
  }

  &:disabled { background: #F5F4F0; cursor: not-allowed; }
`;

const CheckRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.6rem 0;
  cursor: pointer;
  font-size: 0.82rem;
  color: #444;
  line-height: 1.55;

  input {
    margin-top: 0.18rem;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    accent-color: var(--brand-text);
    cursor: pointer;
  }

  a { color: var(--brand-text); text-decoration: underline; }
`;

const SubmitButton = styled.button`
  width: 100%;
  background: var(--brand-text);
  color: #fff;
  border: none;
  border-radius: 0.75rem;
  padding: 0.95rem 1.25rem;
  font-size: 0.95rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  margin-top: 0.75rem;
  transition: background 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) { background: #2a2a2a; }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    background: #333;
  }
`;

const ErrorBox = styled.div`
  background: #FEF3F2;
  border: 1px solid #FDA29B;
  color: #B42318;
  padding: 0.6rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.82rem;
  margin-bottom: 1rem;
  line-height: 1.5;
`;

const FooterNote = styled.div`
  max-width: 560px;
  margin: 2.5rem auto 0;
  padding: 0 1.25rem;
  text-align: center;
  font-size: 0.8rem;
  color: var(--brand-text-muted);
  line-height: 1.7;

  a { color: #555; text-decoration: underline; &:hover { color: var(--brand-text); } }
`;

const SuccessWrap = styled.section`
  max-width: 520px;
  margin: 2.5rem auto 0;
  padding: 2rem;
  background: #fff;
  border: 1px solid #E6E4DE;
  border-radius: 1rem;
  text-align: center;
  animation: ${fadeIn} 0.4s ease both;
`;

const SuccessIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 0.75rem;
`;

const SuccessTitle = styled.h2`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 1.4rem;
  font-weight: 500;
  color: var(--brand-text);
  margin-bottom: 0.5rem;
`;

const SuccessText = styled.p`
  color: #555;
  font-size: 0.92rem;
  line-height: 1.6;
  margin-bottom: 1.25rem;
`;

const ResendButton = styled.button`
  background: transparent;
  border: 1px solid #D0CEC8;
  border-radius: 0.6rem;
  padding: 0.6rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--brand-text);
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) { background: #F5F4F0; border-color: var(--brand-text); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// ============================================================================
// Component: today's look. What the page does is useSignupForm, and what it
// says is SIGNUP_WORDS; the new design's rebuild reads the same two.
// ============================================================================

const REQUIRED = <span style={{ color: '#B42318' }} aria-hidden="true">*</span>;

const Signup: React.FC = () => {
  const {
    form, updateField, formValid, submitting, error, screen,
    handleSubmit, handleResend, resending, resendMessage, goSignIn, continueToSetup,
  } = useSignupForm();
  const F = W.form;

  // --- Success views ----------------------------------------------------------
  // Two variants depending on whether the user opted into SMS:
  //   - SMS opt-in → "Check your phone" double-opt-in flow.
  //   - No SMS    → "Account created" simple confirmation (no Twilio send).
  if (screen === 'created') {
    const C = W.created;
    return (
      <Page>
        <SeoHead
          title={W.seo.created.title}
          description={W.seo.created.description}
          canonicalUrl={W.seo.canonicalUrl}
        />
        <Hero>
          <HeroTitle>{C.title}</HeroTitle>
          <HeroSub>{C.lead}</HeroSub>
        </Hero>
        <SuccessWrap>
          <SuccessIcon aria-hidden="true">✅</SuccessIcon>
          <SuccessTitle>{C.cardTitle}</SuccessTitle>
          <SuccessText>{C.body}</SuccessText>
          <div style={{ marginTop: '1.25rem', fontSize: '0.85rem', color: '#555' }}>
            <button
              type="button"
              onClick={goSignIn}
              style={{ background: 'none', border: 'none', color: 'var(--brand-text)', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.85rem' }}
            >
              {C.signIn}
            </button>
          </div>
        </SuccessWrap>
      </Page>
    );
  }

  if (screen === 'check-phone') {
    const P = W.checkPhone;
    return (
      <Page>
        <SeoHead
          title={W.seo.checkPhone.title}
          description={W.seo.checkPhone.description}
          canonicalUrl={W.seo.canonicalUrl}
        />
        <Hero>
          <HeroTitle>{P.title}</HeroTitle>
          <HeroSub>{P.leadBefore}<strong>{P.yes}</strong>{P.leadAfter}</HeroSub>
        </Hero>
        <SuccessWrap>
          <SuccessIcon aria-hidden="true">📱</SuccessIcon>
          <SuccessTitle>{P.cardTitle}</SuccessTitle>
          <SuccessText>
            {P.replyBefore}<strong>{P.yes}</strong>{P.replyMiddle}<strong>{P.stop}</strong>
            {P.replyStopAfter}<strong>{P.help}</strong>{P.replyEnd}
          </SuccessText>
          <ResendButton
            type="button"
            onClick={continueToSetup}
            style={{ background: 'var(--brand-text)', color: '#fff', borderColor: 'var(--brand-text)', marginRight: '0.6rem' }}
          >
            {P.continue}
          </ResendButton>
          <ResendButton type="button" onClick={handleResend} disabled={resending}>
            {resending ? P.resending : P.resend}
          </ResendButton>
          {resendMessage && (
            <div role="status" aria-live="polite" style={{ marginTop: '0.75rem', fontSize: '0.82rem', color: '#555' }}>
              {resendMessage}
            </div>
          )}
          <div style={{ marginTop: '1.25rem', fontSize: '0.8rem', color: '#888' }}>
            {P.noTextLead}{' '}
            <button
              type="button"
              onClick={goSignIn}
              style={{ background: 'none', border: 'none', color: '#555', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.8rem' }}
            >
              {P.signIn}
            </button>
            {P.noTextEnd}
          </div>
        </SuccessWrap>
      </Page>
    );
  }

  // --- Signup form -----------------------------------------------------------
  return (
    <Page>
      <SeoHead
        title={W.seo.form.title}
        description={W.seo.form.description}
        canonicalUrl={W.seo.canonicalUrl}
      />
      <Hero>
        <HeroTitle>{F.title}</HeroTitle>
        <HeroSub>{F.lead}</HeroSub>
      </Hero>

      <FormCard onSubmit={handleSubmit} noValidate aria-label={F.ariaLabel}>
        {/* aria-live region so screen readers announce server-side and validation errors */}
        <div role="alert" aria-live="assertive">
          {error && <ErrorBox>{error}</ErrorBox>}
        </div>

        <FieldRow>
          <FieldLabel htmlFor="signup-name">
            {F.name} {REQUIRED}
          </FieldLabel>
          <FieldInput
            id="signup-name"
            name="name"
            type="text"
            autoComplete="name"
            required
            value={form.name}
            onChange={(e) => updateField('name', e.target.value)}
            disabled={submitting}
          />
        </FieldRow>

        <FieldRow>
          <FieldLabel htmlFor="signup-email">
            {F.email} {REQUIRED}
          </FieldLabel>
          <FieldInput
            id="signup-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={form.email}
            onChange={(e) => updateField('email', e.target.value)}
            disabled={submitting}
          />
        </FieldRow>

        <FieldRow>
          <FieldLabel htmlFor="signup-phone">
            {F.phone} {REQUIRED}
            <FieldHint>{F.phoneHint}</FieldHint>
          </FieldLabel>
          <FieldInput
            id="signup-phone"
            name="phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            required
            value={form.phone}
            onChange={(e) => updateField('phone', e.target.value)}
            disabled={submitting}
          />
        </FieldRow>

        <FieldRow>
          <FieldLabel htmlFor="signup-firm">
            {F.company} <FieldHint>{F.companyHint}</FieldHint>
          </FieldLabel>
          <FieldInput
            id="signup-firm"
            name="firmName"
            type="text"
            autoComplete="organization"
            value={form.firmName}
            onChange={(e) => updateField('firmName', e.target.value)}
            disabled={submitting}
          />
        </FieldRow>

        <FieldRow>
          <FieldLabel htmlFor="signup-password">
            {F.password} {REQUIRED}
            <FieldHint>{F.passwordHint}</FieldHint>
          </FieldLabel>
          <PasswordInput
            inputComponent={FieldInput}
            id="signup-password"
            name="password"
            autoComplete="new-password"
            required
            minLength={10}
            value={form.password}
            onChange={(e) => updateField('password', e.target.value)}
            disabled={submitting}
          />
        </FieldRow>

        {/* SMS consent — UNCHECKED BY DEFAULT, and OPTIONAL.
            Per Twilio A2P rule 30923, SMS consent cannot be a required
            condition of service: users must be able to create an account
            without ticking this box. The submit button is gated only on the
            ToS checkbox below. */}
        <CheckRow htmlFor="signup-sms-consent">
          <input
            id="signup-sms-consent"
            name="smsConsent"
            type="checkbox"
            checked={form.smsConsent}
            onChange={(e) => updateField('smsConsent', e.target.checked)}
            disabled={submitting}
          />
          <span>
            <strong>{F.sms.optional}</strong>{F.sms.body}
            <a href={F.sms.privacy.href}>{F.sms.privacy.label}</a>{F.sms.and}
            <a href={F.sms.terms.href}>{F.sms.terms.label}</a>{F.sms.end}
          </span>
        </CheckRow>

        {/* Separate ToS/Privacy checkbox — also unchecked by default. */}
        <CheckRow htmlFor="signup-tos">
          <input
            id="signup-tos"
            name="tosAccepted"
            type="checkbox"
            checked={form.tosAccepted}
            onChange={(e) => updateField('tosAccepted', e.target.checked)}
            disabled={submitting}
          />
          <span>
            {F.tos.lead}<a href={F.tos.terms.href}>{F.tos.terms.label}</a>{F.tos.and}
            <a href={F.tos.privacy.href}>{F.tos.privacy.label}</a>{F.tos.end}
          </span>
        </CheckRow>

        <SubmitButton type="submit" disabled={!formValid || submitting} aria-disabled={!formValid || submitting}>
          {submitting ? F.submitting : F.submit}
        </SubmitButton>
      </FormCard>

      <FooterNote>
        {W.footer.haveAccount}{' '}
        <Link to="/auth">{W.footer.signIn}</Link>
        {' '}{W.footer.separator}{' '}
        {W.footer.needCode} <Link to="/start">{W.footer.startTrial}</Link>
      </FooterNote>
    </Page>
  );
};

export default Signup;
